import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import {
    Box,
    Button,
    Card,
    CardBody,
    CardHeader,
    Heading,
    Spinner,
    Text,
    useToast,
    Icon,
    Divider,
} from "@chakra-ui/react";
import { CheckCircleIcon, ArrowBackIcon } from "@chakra-ui/icons";
import { supabase } from "../utils/supabaseClient";
import { COLORS } from "../styles/colors";
import {
    friendlyRpcError,
    buildCorporateParticipantPayload,
    normalizeCorporatePortalUen,
    parseEmployerContactDefaults,
    mergeEmployerDefaultsIntoForm,
} from "./corporateEnrollHelpers";
import AdditionalFields from "../AdditionalFields";
import { loadCorporateEnrollRunCourseContext } from "./loadCorporateEnrollRunCourseContext";
import { CorporateEnrollRunSummaryCard } from "./CorporateEnrollRunSummaryCard";

const CORPORATE_INDIVIDUAL_FORM_INITIAL = {
    participant_name: "",
    participant_email: "",
    registration_type: "",
    course_run_id: "",
    course_type_code: "",
    sponsor_uen: "",
    dob: "",
    displayDob: "",
    nationality: "",
    nric: "",
    mobile_no: "",
    birth_age: "",
    date_signed: "",
};

export function CorporateEnrollIndividualFormPage() {
    const { token, runCode: runCodeParam } = useParams();
    const runCode = runCodeParam ? decodeURIComponent(runCodeParam) : "";
    const toast = useToast();

    const [state, setState] = useState("loading");
    const [errorMessage, setErrorMessage] = useState(null);
    const [portalTitle, setPortalTitle] = useState("");
    const [clientCompanyName, setClientCompanyName] = useState(null);
    const [clientCompanyUen, setClientCompanyUen] = useState(null);
    const [runKind, setRunKind] = useState("");
    const [runLabel, setRunLabel] = useState("");
    const [courseSummary, setCourseSummary] = useState(null);
    const [sessionsFlat, setSessionsFlat] = useState([]);
    const [moduleSessions, setModuleSessions] = useState([]);

    const [templateFieldIds, setTemplateFieldIds] = useState(null);
    const [templateFieldRecords, setTemplateFieldRecords] = useState([]);

    const [formData, setFormData] = useState(CORPORATE_INDIVIDUAL_FORM_INITIAL);
    const [validationErrors, setValidationErrors] = useState({});
    const [selectedFiles, setSelectedFiles] = useState({});
    const [uploadingFiles, setUploadingFiles] = useState(new Set());
    const [registrationId, setRegistrationId] = useState(null);

    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [validateTick, setValidateTick] = useState(0);
    const [additionalFieldsReady, setAdditionalFieldsReady] = useState(false);
    const pendingSubmitRef = useRef(false);

    const listHref = `/corporate-enroll/${token}`;

    useEffect(() => {
        setAdditionalFieldsReady(false);
        setFormData({ ...CORPORATE_INDIVIDUAL_FORM_INITIAL });
    }, [token, runCode]);

    const loadPage = useCallback(async () => {
        if (!token || !runCode) {
            setState("missing");
            return;
        }

        const { data: pub, error: pubErr } = await supabase.rpc("get_corporate_portal_public", {
            p_token: token,
        });
        if (pubErr) throw pubErr;
        if (!pub?.portal) {
            setState("missing");
            return;
        }
        setPortalTitle(pub.portal.title || "");
        setClientCompanyName(
            typeof pub.portal.company_name === "string" ? pub.portal.company_name : null,
        );
        const portalUen = normalizeCorporatePortalUen(pub.portal.company_uen);
        setClientCompanyUen(portalUen || null);
        const runs = Array.isArray(pub.runs) ? pub.runs : [];
        const match = runs.filter((r) => r.run_code === runCode);
        if (match.length === 0) {
            setState("missing");
            return;
        }
        const run = match[0];
        const rk = run.run_kind;
        setRunKind(rk);
        setRunLabel(run.run_label || runCode);

        const [ctx, runPartsRes] = await Promise.all([
            loadCorporateEnrollRunCourseContext(rk, runCode, run.run_label || runCode),
            supabase.rpc("get_corporate_portal_run_participants", {
                p_token: token,
                p_run_kind: rk,
                p_run_code: runCode,
            }),
        ]);
        if (ctx.missing) {
            setState("missing");
            return;
        }
        setRunLabel(ctx.runLabel);
        setCourseSummary(ctx.courseSummary);
        setSessionsFlat(ctx.sessionsFlat);
        setModuleSessions(ctx.moduleSessions);
        setTemplateFieldIds(ctx.templateFieldIds);
        setTemplateFieldRecords(ctx.templateFieldRecords);

        const employerDefaults = parseEmployerContactDefaults(
            runPartsRes.data && typeof runPartsRes.data === "object" && !Array.isArray(runPartsRes.data)
                ? runPartsRes.data.employer_contact_defaults
                : null,
        );
        let merged = mergeEmployerDefaultsIntoForm(
            { ...CORPORATE_INDIVIDUAL_FORM_INITIAL },
            employerDefaults,
            { onlyIfEmpty: false },
        );
        if (!String(merged.sponsor_uen || "").trim() && portalUen) {
            merged = { ...merged, sponsor_uen: portalUen };
        }
        if (!String(merged.registration_type || "").trim()) {
            merged = { ...merged, registration_type: "Company-Sponsored" };
        }
        setFormData(merged);

        setState("ready");
    }, [token, runCode]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setState("loading");
            try {
                await loadPage();
            } catch (e) {
                if (!cancelled) {
                    setErrorMessage(friendlyRpcError(e, "Could not load this page"));
                    setState("error");
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [loadPage]);

    const submitDraft = useCallback(async () => {
        if (!token || !runKind || !runCode) return;
        setSubmitting(true);
        try {
            const payload = buildCorporateParticipantPayload(templateFieldRecords, formData);
            const { error } = await supabase.rpc("corporate_portal_insert_participant_draft", {
                p_token: token,
                p_run_kind: runKind,
                p_run_code: runCode,
                p_participant_data: payload,
            });
            if (error) throw error;
            setSubmitted(true);
            toast({
                title: "Application received",
                description: "Your training provider or HR will follow up if anything else is needed.",
                status: "success",
                duration: 6500,
                isClosable: true,
            });
        } catch (err) {
            toast({
                title: "Could not submit",
                description: friendlyRpcError(err, "Try again or contact your training provider."),
                status: "error",
                duration: 6000,
                isClosable: true,
            });
        } finally {
            setSubmitting(false);
        }
    }, [token, runKind, runCode, templateFieldRecords, formData, toast]);

    const onValidateComplete = useCallback(
        (ok) => {
            if (!pendingSubmitRef.current) return;
            pendingSubmitRef.current = false;
            if (!ok) {
                toast({
                    title: "Please complete required fields",
                    description: "Check highlighted sections below.",
                    status: "warning",
                    duration: 5000,
                    isClosable: true,
                });
                return;
            }
            void submitDraft();
        },
        [submitDraft, toast],
    );

    const handleFormSubmit = (e) => {
        e.preventDefault();
        if (!additionalFieldsReady) {
            toast({
                title: "Still loading the form",
                description: "Please wait a moment and try again.",
                status: "info",
                duration: 4000,
                isClosable: true,
            });
            return;
        }
        pendingSubmitRef.current = true;
        setValidateTick((t) => t + 1);
    };

    const shell = (inner) => (
        <Box minH="100vh" bgGradient="linear(to-b, #F4EEEA, white)" py={{ base: 8, md: 12 }} px={4}>
            <Box maxW="container.md" mx="auto">
                {inner}
            </Box>
        </Box>
    );

    if (state === "loading") {
        return shell(
            <Card>
                <CardBody py={16} textAlign="center">
                    <Spinner size="lg" color={COLORS.mandarin} />
                    <Text mt={4} fontSize="sm" color="gray.600">
                        Loading…
                    </Text>
                </CardBody>
            </Card>,
        );
    }

    if (state === "error") {
        return shell(
            <Card>
                <CardBody py={10}>
                    <Text color="red.600" textAlign="center">
                        {errorMessage}
                    </Text>
                    <Button as={RouterLink} to={listHref} mt={6} leftIcon={<ArrowBackIcon />}>
                        Back to courses
                    </Button>
                </CardBody>
            </Card>,
        );
    }

    if (state === "missing") {
        return shell(
            <Card>
                <CardBody py={10} textAlign="center">
                    <Text color="gray.600">This course is not available on your company link.</Text>
                    <Button as={RouterLink} to={listHref} mt={6} colorScheme="orange">
                        Back to courses
                    </Button>
                </CardBody>
            </Card>,
        );
    }

    if (submitted) {
        return shell(
            <Card shadow="lg" borderWidth="1px">
                <CardBody py={12} textAlign="center">
                    <Icon as={CheckCircleIcon} w={14} h={14} color={COLORS.shinyShamrock} mb={4} />
                    <Heading size="md" color={COLORS.maastrichtBlue} mb={2}>
                        Thank you
                    </Heading>
                    <Text color="gray.600" fontSize="sm" maxW="lg" mx="auto" mb={6}>
                        Your details were saved for this course. To register for another course from the same company
                        portal, go back to the list and open the next run — submit one application per course.
                    </Text>
                    <Button as={RouterLink} to={listHref} colorScheme="orange">
                        Choose another course
                    </Button>
                </CardBody>
            </Card>,
        );
    }

    return (
        <Box minH="100vh" bgGradient="linear(to-b, #F4EEEA, white)" py={{ base: 8, md: 12 }} px={4}>
            <Box maxW="container.md" mx="auto">
                <Button
                    as={RouterLink}
                    to={listHref}
                    variant="ghost"
                    size="sm"
                    leftIcon={<ArrowBackIcon />}
                    mb={6}
                    color="gray.600"
                >
                    All courses
                </Button>

                <CorporateEnrollRunSummaryCard
                    portalTitle={portalTitle}
                    clientCompanyName={clientCompanyName}
                    clientCompanyUen={clientCompanyUen}
                    courseSummary={courseSummary}
                    runLabel={runLabel}
                    runCode={runCode}
                    moduleSessions={moduleSessions}
                    sessionsFlat={sessionsFlat}
                />

                <Card shadow="md" borderWidth="1px">
                    <CardHeader>
                        <Heading size="sm" color={COLORS.maastrichtBlue}>
                            Registration
                        </Heading>
                        <Text fontSize="sm" color="gray.600" mt={1}>
                            Fields follow your organisation&apos;s registration template for this program (same structure
                            as public course registration).
                        </Text>
                    </CardHeader>
                    <CardBody>
                        <Box as="form" onSubmit={handleFormSubmit}>
                            <AdditionalFields
                                key={`${token}-${runCode}`}
                                formData={formData}
                                setFormData={setFormData}
                                validationErrors={validationErrors}
                                setValidationErrors={setValidationErrors}
                                registrationId={registrationId}
                                setRegistrationId={setRegistrationId}
                                templateFieldIds={templateFieldIds}
                                selectedFiles={selectedFiles}
                                setSelectedFiles={setSelectedFiles}
                                uploadingFiles={uploadingFiles}
                                setUploadingFiles={setUploadingFiles}
                                corporateEnrollMode
                                corporateValidateTick={validateTick}
                                corporateOnValidateComplete={onValidateComplete}
                                onFieldsLoaded={() => setAdditionalFieldsReady(true)}
                            />
                            <Divider my={8} />
                            <Button
                                type="submit"
                                w="100%"
                                bg={COLORS.mandarin}
                                color="white"
                                _hover={{ bg: "#d96a3d" }}
                                size="lg"
                                isLoading={submitting}
                                loadingText="Submitting"
                                isDisabled={!additionalFieldsReady}
                            >
                                Submit application
                            </Button>
                        </Box>
                    </CardBody>
                </Card>
            </Box>
        </Box>
    );
}
